import { Component, OnInit } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { UrlSignature } from '../common/apiEndpoint';

export interface OldApplicant {
  personId: string;
  applicantName: string;
  programmeType: string;
  course: string | null;
  programme: string;
  registrationNumber: string;
  country: string;
  hometown: string;
  gender: string;
  applicationReceivedDate: string;
  status: string;
  nationality: string;
  applicantType: string;
  semester: string | null;
  academicYear: string;
}

@Component({
  selector: 'app-applicantfromoldapp',
  templateUrl: './applicantfromoldapp.component.html',
  styleUrls: ['./applicantfromoldapp.component.scss'],
  providers: [UrlSignature],
})
export class ApplicantfromoldappComponent implements OnInit {
  applicants: OldApplicant[] = [];
  filteredApplicants: OldApplicant[] = [];

  isLoading = true;
  errorMessage = '';
  searchQuery = '';
  activeFilter = 'ALL';

  selectedYear: string = new Date().getFullYear().toString();
  availableYears: string[] = ['2026', '2025', '2024', '2023'];

  // Status update state
  updatingId: string | null = null;
  toastMessage = '';
  toastType: 'success' | 'error' = 'success';
  showToast = false;

  // Pagination
  currentPage = 1;
  pageSize = 10;

  // Confirmation modal
  showConfirmModal = false;
  pendingApplicant: OldApplicant | null = null;
  pendingStatus: string = '';

  readonly filters = ['ALL', 'PENDING', 'UNDER REVIEW', 'ACCEPTED', 'REJECTED', 'WAITLISTED'];

  constructor(private http: HttpClient, private urls: UrlSignature) {}

  ngOnInit(): void {
    this.loadApplicants();
  }

  // ── Data Loading ──────────────────────────────────────────────────────────

  loadApplicants(): void {
    this.isLoading = true;
    this.errorMessage = '';

    const token = localStorage.getItem('token') || sessionStorage.getItem('token') || '';
    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
    const url = `${this.urls.retrievealloldapplicanturl}${this.selectedYear}`;

    this.http.get<OldApplicant[]>(url, { headers }).subscribe({
      next: (data) => {
        this.applicants = data;
        this.applyFilters();
        this.isLoading = false;
      },
      error: (err) => {
        this.errorMessage = 'Failed to load applicants. Please try again.';
        this.isLoading = false;
        console.error('Error fetching old applicants:', err);
      },
    });
  }

  onYearChange(): void {
    this.loadApplicants();
  }

  // ── Filtering & Search ────────────────────────────────────────────────────

  applyFilters(): void {
    let results = [...this.applicants];

    if (this.activeFilter !== 'ALL') {
      const filterKey = this.activeFilter.replace(' ', '_');
      results = results.filter(
        (a) => a.status?.toUpperCase() === filterKey || a.status?.toUpperCase() === this.activeFilter
      );
    }

    if (this.searchQuery.trim()) {
      const q = this.searchQuery.toLowerCase();
      results = results.filter(
        (a) =>
          a.applicantName?.toLowerCase().includes(q) ||
          a.programmeType?.toLowerCase().includes(q) ||
          a.personId?.toLowerCase().includes(q) ||
          a.registrationNumber?.toLowerCase().includes(q)
      );
    }

    this.filteredApplicants = results;
    this.currentPage = 1;
  }

  setFilter(filter: string): void {
    this.activeFilter = filter;
    this.applyFilters();
  }

  onSearch(): void {
    this.applyFilters();
  }

  // ── Pagination ────────────────────────────────────────────────────────────

  get totalPages(): number {
    return Math.ceil(this.filteredApplicants.length / this.pageSize) || 1;
  }

  get paginatedApplicants(): OldApplicant[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.filteredApplicants.slice(start, start + this.pageSize);
  }

  getPageStart(): number {
    if (this.filteredApplicants.length === 0) return 0;
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  getPageEnd(): number {
    return Math.min(this.currentPage * this.pageSize, this.filteredApplicants.length);
  }

  getPageNumbers(): number[] {
    return Array.from({ length: this.totalPages }, (_, i) => i + 1);
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
  }

  // ── Status Update ─────────────────────────────────────────────────────────

  /**
   * Entry point for all action buttons.
   * ACCEPTED → opens confirmation modal first.
   * All others → updates directly.
   */
  requestStatusUpdate(applicant: OldApplicant, newStatus: string): void {
    if (newStatus === 'ACCEPTED') {
      this.pendingApplicant = applicant;
      this.pendingStatus = newStatus;
      this.showConfirmModal = true;
    } else {
      this.updateStatus(applicant, newStatus);
    }
  }

  /** Modal: accept without generating letter */
  confirmWithoutLetter(): void {
    if (!this.pendingApplicant) return;
    this.showConfirmModal = false;
    this.updateStatus(this.pendingApplicant, this.pendingStatus);
    this.pendingApplicant = null;
  }

  /** Modal: accept AND generate/download the provisional admission letter */
  confirmWithLetter(): void {
    if (!this.pendingApplicant) return;
    const applicant = this.pendingApplicant;
    this.showConfirmModal = false;
    this.updateStatus(applicant, this.pendingStatus, () => {
      this.generateAndDownloadLetter(applicant);
    });
    this.pendingApplicant = null;
  }

  cancelConfirm(): void {
    this.showConfirmModal = false;
    this.pendingApplicant = null;
    this.pendingStatus = '';
  }

  updateStatus(applicant: OldApplicant, newStatus: string, onSuccess?: () => void): void {
    if (this.updatingId === applicant.personId) return;

    this.updatingId = applicant.personId;

    const token = localStorage.getItem('token') || sessionStorage.getItem('token') || '';
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    });

    const payload = { personId: applicant.personId, status: newStatus };

    this.http
      .post(this.urls.oldApplicantRegistryStatusUpdateUrl, payload, {
        headers,
        observe: 'response',       // treat all 2xx (incl. 204 NO_CONTENT) as success
        responseType: 'text' as 'json',
      })
      .subscribe({
        next: () => {
          applicant.status = newStatus;
          this.applyFilters();
          this.updatingId = null;
          this.showToastMessage(`${applicant.applicantName} marked as ${newStatus}.`, 'success');
          if (onSuccess) onSuccess();
        },
        error: (err) => {
          this.updatingId = null;
          this.showToastMessage('Status update failed. Please try again.', 'error');
          console.error('Status update error:', err);
        },
      });
  }

  showToastMessage(msg: string, type: 'success' | 'error'): void {
    this.toastMessage = msg;
    this.toastType = type;
    this.showToast = true;
    setTimeout(() => (this.showToast = false), 3500);
  }

  // ── Letter Generation (client-side via docx library) ─────────────────────

  generateAndDownloadLetter(applicant: OldApplicant): void {
    import('docx').then(({
      Document, Packer, Paragraph, TextRun, ImageRun,
      AlignmentType, WidthType, Table, TableRow, TableCell, ShadingType,
    }) => {
      const today = new Date().toLocaleDateString('en-GB', {
        day: 'numeric', month: 'long', year: 'numeric',
      });

      fetch('assets/images/stephanie_signature.png')
        .then(r => r.arrayBuffer())
        .then(sigBuffer => {

          const doc = new Document({
            sections: [{
              properties: {
                page: {
                  size: { width: 11906, height: 16838 }, // A4
                  margin: { top: 1440, right: 1440, bottom: 1440, left: 1440 },
                },
              },
              children: [

                // Date (right-aligned)
                new Paragraph({
                  alignment: AlignmentType.RIGHT,
                  spacing: { after: 200 },
                  children: [new TextRun({ text: today, font: 'Arial', size: 22 })],
                }),

                // Salutation
                new Paragraph({
                  spacing: { after: 200 },
                  children: [new TextRun({ text: `Dear ${applicant.applicantName},`, font: 'Arial', size: 22 })],
                }),

                // Subject line
                new Paragraph({
                  alignment: AlignmentType.CENTER,
                  spacing: { after: 200 },
                  children: [new TextRun({
                    text: 'OFFER OF PROVISIONAL ADMISSION',
                    font: 'Arial', size: 24, bold: true, underline: {},
                  })],
                }),

                // Body paragraph
                new Paragraph({
                  spacing: { after: 160 },
                  alignment: AlignmentType.JUSTIFIED,
                  children: [
                    new TextRun({ text: 'The Central Admissions Committee, with reference to your application, is delighted to offer you provisional admission to pursue the ', font: 'Arial', size: 22 }),
                    new TextRun({ text: applicant.programmeType || 'DEGREE', font: 'Arial', size: 22, bold: true }),
                    new TextRun({ text: ` Programme commencing the ${applicant.academicYear || new Date().getFullYear()} academic year. Your Provisional Student Number is: `, font: 'Arial', size: 22 }),
                    new TextRun({ text: applicant.personId, font: 'Arial', size: 22, bold: true }),
                    new TextRun({ text: '. Kindly take note of the following conditions carefully:', font: 'Arial', size: 22 }),
                  ],
                }),

                // Bullet conditions
                ...[
                  'If at any time the University discovers that you do not possess the qualifications by virtue of which you have been given admission, you will be withdrawn.',
                  'Upon acceptance of this offer, you will be required to pay not less than 75% of the total fees for the semester before registration.',
                  'Full payment of fees for the Semester is expected before the End-of-Semester examinations begin. Please quote your Provisional Student Number when making payment.',
                  'All fresh students are required to take a medical examination from a reputable hospital and submit the report during registration.',
                  'Make sure to submit the original copy of your results during registration.',
                  'You will be on probation for the full duration of your programme and may be dismissed at any time for unsatisfactory academic performance or grievous misconduct.',
                ].map(text => new Paragraph({
                  spacing: { after: 120 },
                  indent: { left: 360, hanging: 360 },
                  children: [
                    new TextRun({ text: '\u2022 ', font: 'Arial', size: 22 }),
                    new TextRun({ text, font: 'Arial', size: 22 }),
                  ],
                })),

                // Closing
                new Paragraph({
                  spacing: { before: 200, after: 160 },
                  children: [new TextRun({ text: 'Once again, please accept our congratulations. We look forward to seeing you.', font: 'Arial', size: 22 })],
                }),
                new Paragraph({
                  spacing: { after: 80 },
                  children: [new TextRun({ text: 'Yours faithfully,', font: 'Arial', size: 22 })],
                }),

                // Signature image
                new Paragraph({
                  spacing: { after: 40 },
                  children: [new ImageRun({
                    data: sigBuffer,
                    transformation: { width: 150, height: 58 },
                    type: 'jpg',
                  })],
                }),

                // Registrar
                new Paragraph({
                  spacing: { after: 40 },
                  children: [new TextRun({ text: 'Ms. Stephanie Acquah \u2013 Djan', font: 'Arial', size: 22, bold: true })],
                }),
                new Paragraph({
                  spacing: { after: 400 },
                  children: [new TextRun({ text: 'Registrar', font: 'Arial', size: 22 })],
                }),

                // Fee table heading
                new Paragraph({
                  alignment: AlignmentType.CENTER,
                  spacing: { before: 200, after: 160 },
                  children: [new TextRun({ text: 'FEE PAYMENT SCHEDULES PER SEMESTER', font: 'Arial', size: 22, bold: true })],
                }),

                // Fee table
                new Table({
                  width: { size: 9026, type: WidthType.DXA },
                  columnWidths: [6000, 3026],
                  rows: [
                    new TableRow({
                      tableHeader: true,
                      children: [
                        new TableCell({
                          width: { size: 6000, type: WidthType.DXA },
                          shading: { fill: '630e12', type: ShadingType.CLEAR },
                          margins: { top: 80, bottom: 80, left: 120, right: 120 },
                          children: [new Paragraph({ children: [new TextRun({ text: 'DESCRIPTION', font: 'Arial', size: 20, bold: true, color: 'FFFFFF' })] })],
                        }),
                        new TableCell({
                          width: { size: 3026, type: WidthType.DXA },
                          shading: { fill: '630e12', type: ShadingType.CLEAR },
                          margins: { top: 80, bottom: 80, left: 120, right: 120 },
                          children: [new Paragraph({ alignment: AlignmentType.RIGHT, children: [new TextRun({ text: 'AMOUNT (GH\u20b5)', font: 'Arial', size: 20, bold: true, color: 'FFFFFF' })] })],
                        }),
                      ],
                    }),
                    ...[
                      ['Tuition Fees',                                '1,697.50', false],
                      ['Other Charges',                               '702.50',   false],
                      ['Total',                                       '2,400.00', true ],
                      ['Minimum fee before Registration (75%)',       '1,800.00', false],
                      ['Balance before end-of-semester exams (25%)', '600.00',   false],
                    ].map(([label, amount, bold], i) => new TableRow({
                      children: [
                        new TableCell({
                          width: { size: 6000, type: WidthType.DXA },
                          shading: { fill: i % 2 === 0 ? 'F7F5F2' : 'FFFFFF', type: ShadingType.CLEAR },
                          margins: { top: 80, bottom: 80, left: 120, right: 120 },
                          children: [new Paragraph({ children: [new TextRun({ text: label as string, font: 'Arial', size: 20, bold: bold as boolean })] })],
                        }),
                        new TableCell({
                          width: { size: 3026, type: WidthType.DXA },
                          shading: { fill: i % 2 === 0 ? 'F7F5F2' : 'FFFFFF', type: ShadingType.CLEAR },
                          margins: { top: 80, bottom: 80, left: 120, right: 120 },
                          children: [new Paragraph({ alignment: AlignmentType.RIGHT, children: [new TextRun({ text: amount as string, font: 'Arial', size: 20, bold: bold as boolean })] })],
                        }),
                      ],
                    })),
                  ],
                }),

                // Payment info
                new Paragraph({
                  spacing: { before: 200, after: 80 },
                  children: [new TextRun({ text: 'FEE PAYMENT', font: 'Arial', size: 22, bold: true })],
                }),
                new Paragraph({
                  spacing: { after: 80 },
                  children: [new TextRun({ text: 'Students may pay fees online at payments.techbridge.edu.gh or at any branch of Consolidated Bank Ghana Ltd.', font: 'Arial', size: 22 })],
                }),
                new Paragraph({
                  spacing: { after: 40 },
                  children: [new TextRun({ text: 'Branch Name: Kokomlemle Branch', font: 'Arial', size: 22 })],
                }),
                new Paragraph({
                  spacing: { after: 200 },
                  children: [new TextRun({ text: 'Account Number: 0600779100001', font: 'Arial', size: 22, bold: true })],
                }),

              ],
            }],
          });

          Packer.toBlob(doc).then(blob => {
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Admission_Letter_${applicant.applicantName.replace(/\s+/g, '_')}.docx`;
            a.click();
            URL.revokeObjectURL(url);
          });
        });
    });
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  getInitials(name: string): string {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    return parts.length >= 2
      ? (parts[0][0] + parts[1][0]).toUpperCase()
      : parts[0][0].toUpperCase();
  }

  getAvatarColor(name: string): string {
    const colors = [
      '#630e12', '#8b1a1f', '#a52329', '#c0392b',
      '#2c3e50', '#34495e', '#1a5276', '#1f618d',
    ];
    let hash = 0;
    for (let i = 0; i < name?.length || 0; i++) hash += name.charCodeAt(i);
    return colors[hash % colors.length];
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  }

  // ── Stat helpers ──────────────────────────────────────────────────────────

  get totalCount(): number { return this.applicants.length; }
  get pendingCount(): number { return this.countByStatus('PENDING'); }
  get underReviewCount(): number { return this.countByStatus('UNDER_REVIEW') + this.countByStatus('UNDER REVIEW'); }
  get acceptedCount(): number { return this.countByStatus('ACCEPTED'); }
  get rejectedCount(): number { return this.countByStatus('REJECTED'); }
  get waitlistedCount(): number { return this.countByStatus('WAITLISTED'); }

  private countByStatus(status: string): number {
    return this.applicants.filter((a) => a.status?.toUpperCase() === status).length;
  }
}